import React from "react";
import { X, Maximize2, Minimize2, Download, Clipboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArtifactItem, ArtifactLoadingPlaceholder } from "@/types/Artifact";

interface ArtifactPanelHeaderProps {
  activeItem: ArtifactItem | ArtifactLoadingPlaceholder | undefined;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
  onDownloadChart?: () => void;
  onCopyChart?: () => void;
  onDownloadTable?: () => void;
  onCopyTable?: () => void;
}

export const ArtifactPanelHeader: React.FC<ArtifactPanelHeaderProps> = React.memo(({
  activeItem,
  isFullscreen,
  onToggleFullscreen,
  onClose,
  onDownloadChart,
  onCopyChart,
  onDownloadTable,
  onCopyTable,
}) => {
  const showChartActions = activeItem && 
    !('isLoading' in activeItem) && 
    activeItem.type === "graph";

  const showTableActions = activeItem && 
    !('isLoading' in activeItem) && 
    activeItem.type === "table";

  return (
    <div className="flex items-center justify-between border-b p-4">
      <h2 className="font-semibold">Slates</h2>
      <div className="flex gap-2">
        {/* Chart specific actions */}
        {showChartActions && (
          <>
            <div className="flex gap-1">
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onDownloadChart}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download Chart</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onCopyChart}
                    >
                      <Clipboard className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy Chart</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Separator orientation="vertical" className="h-7" />
          </>
        )}

        {/* Table specific actions */}
        {showTableActions && (
          <>
            <div className="flex gap-1">
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onDownloadTable}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download as CSV</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onCopyTable}
                    >
                      <Clipboard className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy Table</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Separator orientation="vertical" className="h-7" />
          </>
        )}
        
        {/* General actions */}
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hidden md:inline-flex"
                  onClick={onToggleFullscreen}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Close</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
});